/**
 * signInReturn: where a shopper lands after signing in or creating an account.
 *
 * Every door into /login and /register goes through here, so a shopper who
 * leaves a cart, a checkout step, a storefront or a product to sign in comes
 * back to that exact place (path, query and hash), not the homepage.
 *
 * Three carriers, read in this order, because each one survives a hop the
 * others do not:
 *   1. location.state.from, set by ProtectedRoute. Keeps the original
 *      location's own state, but is dropped by a plain <Link> to /register.
 *   2. ?redirect=<path> on /login and /register. Survives the login and
 *      register links and a page refresh.
 *   3. sessionStorage, written whenever a return path is recorded. Survives
 *      anything inside the tab, including a full page load.
 *
 * Only same-site paths are honoured ("/..." and never "//..."), so the query
 * parameter cannot be used to bounce a shopper to another website.
 */

const RETURN_STORAGE_KEY = "lusuku.signInReturn";

/* Pages that are themselves part of signing in: never a place to return to. */
const AUTH_PATH_PREFIXES = [
  "/login",
  "/register",
  "/forgot-password",
  "/passwordResetReq",
  "/pwdResetForm",
];

const isAuthPath = (path) =>
  AUTH_PATH_PREFIXES.some(
    (prefix) =>
      path === prefix ||
      path.startsWith(`${prefix}/`) ||
      path.startsWith(`${prefix}?`) ||
      path.startsWith(`${prefix}#`)
  );

export const isSafeReturnPath = (path) =>
  typeof path === "string" &&
  path.startsWith("/") &&
  !path.startsWith("//") &&
  !path.includes("\\") &&
  !isAuthPath(path);

/* "/brand/shop/x?outlet=y#reviews" from a router or window location. */
export const pathOfLocation = (location) => {
  if (!location || typeof location !== "object") return "";
  return `${location.pathname || ""}${location.search || ""}${location.hash || ""}`;
};

export const currentBrowserPath = () => {
  if (typeof window === "undefined") return "/";
  return pathOfLocation(window.location) || "/";
};

/*
 * Record (or, for the homepage or an unsafe path, forget) the place to come
 * back to. Called when the shopper actually heads to sign in, never during a
 * render, so the stored copy is always the last real intent.
 */
export const recordSignInReturn = (path) => {
  try {
    if (isSafeReturnPath(path) && path !== "/") {
      sessionStorage.setItem(RETURN_STORAGE_KEY, path);
    } else {
      sessionStorage.removeItem(RETURN_STORAGE_KEY);
    }
  } catch (_) {
    /* storage disabled: the query parameter still carries the path */
  }
};

export const clearSignInReturn = () => {
  try {
    sessionStorage.removeItem(RETURN_STORAGE_KEY);
  } catch (_) {
    /* nothing to clear */
  }
};

const storedSignInReturn = () => {
  try {
    const stored = sessionStorage.getItem(RETURN_STORAGE_KEY);
    return isSafeReturnPath(stored) ? stored : null;
  } catch (_) {
    return null;
  }
};

/**
 * The router target to send a just-signed-in shopper to. Returns a location
 * object when the original location (with its own state) is known, else a
 * path string, else "/".
 */
export const signInReturnTarget = (location) => {
  const from = location?.state?.from;
  if (from && typeof from === "object" && isSafeReturnPath(pathOfLocation(from))) {
    return {
      pathname: from.pathname,
      search: from.search || "",
      hash: from.hash || "",
      state: from.state,
    };
  }
  const redirect = new URLSearchParams(location?.search || "").get("redirect");
  if (isSafeReturnPath(redirect)) return redirect;
  if (isSafeReturnPath(from)) return from;
  return storedSignInReturn() || "/";
};

/* The same answer as a plain path, for links that carry it onward. */
export const signInReturnPath = (location) => {
  const target = signInReturnTarget(location);
  return typeof target === "string" ? target : pathOfLocation(target);
};

/**
 * Where to send a shopper who has to sign in: /login (or /register) carrying
 * the place to come back to. Pure, so it is safe to call while rendering a
 * link; pair it with recordSignInReturn when the shopper actually goes.
 */
export const signInLocation = (returnPath, authPathname = "/login") => {
  if (!isSafeReturnPath(returnPath) || returnPath === "/") {
    return { pathname: authPathname };
  }
  return {
    pathname: authPathname,
    search: `?redirect=${encodeURIComponent(returnPath)}`,
  };
};

/* history.push to sign-in (or register) from wherever the shopper is now. */
export const goToSignIn = (history, returnPath = currentBrowserPath(), authPathname = "/login") => {
  recordSignInReturn(returnPath);
  history.push(signInLocation(returnPath, authPathname));
};
